import React, { useMemo } from 'react';
import styled from 'styled-components/macro';

import {
  TableHeader,
  TableRow,
  HorizontalScrollContainer,
} from 'components/Overview/BreakdownTable/styles';
import { AlignContent } from 'components/data-containers/TerritoryTable/shared/TerritoryTable.style';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper/index';
import CountryFlag from 'components/base/CountryFlag/index';
import DoubleMetricCell from 'components/data-visualization/Table/DoubleMetricCell';
import TableLoader from 'components/utility/ContentLoader/TableLoader/TableLoader';
import Table from 'components/data-visualization/Table/Table';

import metrics from 'maps/metrics';
import useMedia from 'hooks/useMedia';
import useRadioSpinsData from './utils/useRadioSpinsData';

import BreakdownTablePlaceholder from 'components/data-visualization/Table/BreakdownTablePlaceholder';
import Layout from 'components/styled/Layout/Layout';
import { IRadioSpinsItem, IValues } from './types';
import { HeaderWrapper } from 'components/data-visualization/Table/DropdownHead';
import { MAX_COUNTRY_NAME } from 'config';
import TooltipWrapper from 'components/base/TooltipWrapper';
import getShortName from 'helpers/getShortName';

interface ICellContent {
  [key: string]: any;
}

const CellContent = styled(AlignContent)<ICellContent>`
  font-weight: ${({ isActive }): number => (isActive ? 600 : 500)};
`;

interface IRadioTable {
  activeMetric: string;
  isPending: boolean;
  headers: string[];
  radioSpinsData: IRadioSpinsItem[];
  filteredCountryId?: string;
}

interface IRow {
  countryId: string;
  displayName: string;
  flagImgLarge: string;
  flagImgMedium: string;
  flagImgSmall: string;
  id: string;
  lat: number;
  lon: number;
  values: IValues;
}

const RadioTable: React.FC<IRadioTable> = (props) => {
  const {
    headers,
    radioSpinsData,
    isPending,
    activeMetric,
    filteredCountryId,
  } = props;

  const rows = useRadioSpinsData(radioSpinsData) || [];
  const mqMatches = useMedia('(min-width: 800px)');

  /**
   * True if there are no breakdowns to display.
   * @returns {boolean}
   */
  const hasNoData = useMemo(() => {
    if (Array.isArray(rows) && rows) {
      return rows.length === 0;
    }
    return !isPending;
  }, [rows, isPending]);

  return (
    <HorizontalScrollContainer>
      <AnimatedLoadingWrapper loading={isPending}>
        <Layout.Relative>
          <Table>
            <thead>
              <tr>
                {headers.map((header, index) => {
                  const condition = header
                    .toLowerCase()
                    .includes('this week to date');
                  return (
                    <TableHeader key={header} isRightAligned>
                      <HeaderWrapper
                        key={`${header}${index}`}
                        condition={condition}
                      >
                        {header}
                      </HeaderWrapper>
                    </TableHeader>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {isPending && <TableLoader columnCount={headers.length} />}
              {hasNoData && !isPending && (
                <BreakdownTablePlaceholder
                  isSmallScreen={!mqMatches}
                  columnCount={headers.length}
                />
              )}
              {rows &&
                !isPending &&
                rows.map(
                  (row: IRow): JSX.Element => {
                    const countryDisplayName = mqMatches
                      ? getShortName(row.displayName)
                      : row.id;
                    return (
                      <TableRow
                        key={`radio-breakdown-row-${row.id}}`}
                        isRightAligned
                      >
                        <td>
                          <CellContent
                            alignLeft
                            isActive={row.id === filteredCountryId}
                          >
                            <CountryFlag
                              id={row.id}
                              displayName={row.displayName}
                              flagImgSmall={row.flagImgSmall}
                              flagImgMedium={row.flagImgMedium}
                              flagImgLarge={row.flagImgLarge}
                              small
                            />
                            <TooltipWrapper
                              condition={
                                mqMatches &&
                                row.displayName.length > MAX_COUNTRY_NAME
                              }
                              tooltipContent={row.displayName}
                            >
                              {countryDisplayName}
                            </TooltipWrapper>
                          </CellContent>
                        </td>

                        <DoubleMetricCell
                          entityData={row.values}
                          header={metrics[activeMetric]}
                          sortBy={metrics[activeMetric]}
                        />
                      </TableRow>
                    );
                  }
                )}
            </tbody>
          </Table>
        </Layout.Relative>
      </AnimatedLoadingWrapper>
    </HorizontalScrollContainer>
  );
};

export default RadioTable;
